import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Nav, Navbar, NavbarBrand, NavbarToggler, Collapse, Container, NavItem, NavLink } from 'reactstrap';
import MainMenuItem from './mainMenuItem';
import FixedHeader from './fixedHeader';
import HierarchicalHeader from './hierarchicalHeader';
import TruLogo from './TruLogo';
import entityComposition from '../common/classes/entityComposition';
import appHistory from '../../utils/appHistory';
import Icon from '../common/containers/icon';
import FavoritesModal from '../favorites/favorites';
import { getAllFavorites, filterFavorites } from '../favorites/favoritesHelpers';
import { useUserPreferencesFavorites } from '../favorites/userPreferencesFavorites';
const handleLogout = event => {
  if (event !== null && event !== void 0 && event.preventDefault) {
    // MainMenuItem defaults href to "#"; keep /logout navigation authoritative.
    event.preventDefault();
  }
  appHistory.push('/logout');
};

// Custom hook for loading settings and config behaviors
const useHeaderInitialization = (cabinets, configBehaviorActions, settingsActions) => {
  useEffect(() => {
    // only need to load CustomerSettings and ConfigBehaviors once per
    // browser session. Reloading the page will reload the settings.
    if (cabinets) {
      return;
    }
    settingsActions.serverLoad();
    configBehaviorActions.serverIndex({});
  }, [cabinets, configBehaviorActions, settingsActions]);
};
const Header = ({
  configBehaviorActions,
  dashboards: _dashboards = [],
  cabinets: _cabinets = null,
  settingsActions,
  settingsLoaded,
  user,
  userContent: _userContent = null,
  userActions: _userActions = {},
  tabGroups: _tabGroups = [],
  favoritesEnabled: _favoritesEnabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const showIcons = true; // Top nav always shows icons (layout+mode selector controls shell/theme)
  const [favoritesModalOpen, setFavoritesModalOpen] = useState(false);
  const [favoritesQuery, setFavoritesQuery] = useState('');
  const {
    preferencesContent,
    ensureLoaded: ensurePreferencesFavoritesLoaded
  } = useUserPreferencesFavorites();
  const userId = user === null || user === void 0 ? void 0 : user.user_id;
  const loadUserSavedViews = _userActions.serverShow;
  useEffect(() => {
    if (!_favoritesEnabled || !userId || !settingsLoaded || !favoritesModalOpen) {
      return;
    }
    // Re-fetch from server so metaDeleted-form pins pruned since the last cached load are gone.
    ensurePreferencesFavoritesLoaded({
      force: true
    }).catch(() => {});
    if (loadUserSavedViews && _userContent == null) {
      loadUserSavedViews(userId).catch(() => {});
    }
  }, [_favoritesEnabled, userId, settingsLoaded, favoritesModalOpen, ensurePreferencesFavoritesLoaded, loadUserSavedViews, _userContent]);
  const filteredFavorites = useMemo(() => {
    if (!user) return [];
    const cabinetDefinitions = _cabinets || [];
    const allFavorites = getAllFavorites(_userContent, cabinetDefinitions, preferencesContent);
    return filterFavorites(allFavorites, favoritesQuery);
  }, [user, _userContent, _cabinets, favoritesQuery, preferencesContent]);
  const handleOpenFavorites = () => {
    setFavoritesModalOpen(true);
    setFavoritesQuery('');
  };
  const handleCloseFavorites = () => {
    setFavoritesModalOpen(false);
    setFavoritesQuery('');
  };
  useHeaderInitialization(_cabinets, configBehaviorActions, settingsActions);
  const hasGroupedCabinets = _cabinets && _cabinets.some(cabinet => {
    var _cabinet$client;
    return (cabinet === null || cabinet === void 0 || (_cabinet$client = cabinet.client) === null || _cabinet$client === void 0 || (_cabinet$client = _cabinet$client.web) === null || _cabinet$client === void 0 || (_cabinet$client = _cabinet$client.tabGroup) === null || _cabinet$client === void 0 ? void 0 : _cabinet$client.identifier) && cabinet.client.web.tabGroup.identifier !== 'admin';
  });
  const refersLegacyCabinets = _cabinets && _cabinets.some(cabinet => cabinet.identifier.includes('legacy'));
  const hasDirectFormCreation = _cabinets && _cabinets.some(cabinet => !!(cabinet !== null && cabinet !== void 0 && cabinet.directFormType));
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Navbar, {
    light: true,
    className: "navbar-default",
    expand: "md"
  }, /*#__PURE__*/React.createElement(Container, null, /*#__PURE__*/React.createElement(NavbarBrand, {
    href: "/"
  }, /*#__PURE__*/React.createElement(TruLogo, {
    width: "80px"
  })), /*#__PURE__*/React.createElement(NavbarToggler, {
    onClick: () => setIsOpen(!isOpen),
    className: "mr-2"
  }), /*#__PURE__*/React.createElement(Collapse, {
    isOpen: isOpen,
    navbar: true
  }, /*#__PURE__*/React.createElement(Nav, {
    tabs: true,
    navbar: true,
    className: "ml-auto navbar-right"
  }, hasGroupedCabinets || refersLegacyCabinets || hasDirectFormCreation ? /*#__PURE__*/React.createElement(HierarchicalHeader, {
    user: user,
    cabinets: _cabinets,
    dashboards: _dashboards,
    tabGroups: _tabGroups,
    settingsLoaded: settingsLoaded
  }) : /*#__PURE__*/React.createElement(FixedHeader, {
    user: user,
    cabinets: _cabinets,
    dashboards: _dashboards,
    settingsLoaded: settingsLoaded
  }), _favoritesEnabled && /*#__PURE__*/React.createElement(NavItem, {
    className: "t-h-nav-favorites",
    style: {
      textAlign: 'center'
    }
  }, /*#__PURE__*/React.createElement(NavLink, {
    href: "#",
    onClick: event => {
      event.preventDefault();
      handleOpenFavorites();
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    glyph: "star"
  }), /*#__PURE__*/React.createElement("br", null), "Favorites")), /*#__PURE__*/React.createElement(MainMenuItem, {
    className: "t-h-nav-logout",
    key: "logoutTab",
    href: "/#/logout",
    onClick: handleLogout,
    glyph: showIcons ? 'sign-out-alt' : '',
    title: "Log Out"
  }))))), _favoritesEnabled && /*#__PURE__*/React.createElement(FavoritesModal, {
    isOpen: favoritesModalOpen,
    onClose: handleCloseFavorites,
    query: favoritesQuery,
    onQueryChange: setFavoritesQuery,
    favorites: filteredFavorites
  }));
};
Header.propTypes = {
  cabinets: PropTypes.arrayOf(PropTypes.shape({
    glyph: PropTypes.string,
    directFormType: PropTypes.string
  })),
  configBehaviorActions: PropTypes.shape({
    serverIndex: PropTypes.func
  }).isRequired,
  settingsActions: PropTypes.shape({
    serverLoad: PropTypes.func
  }).isRequired,
  settingsLoaded: PropTypes.bool.isRequired,
  dashboards: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    embedID: PropTypes.string
  })),
  tabGroups: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string,
    identifier: PropTypes.string,
    icon: PropTypes.string
  })),
  user: PropTypes.shape({
    permissions: PropTypes.shape({
      formTypes: PropTypes.shape({}),
      general: PropTypes.arrayOf(PropTypes.string)
    }),
    user_id: PropTypes.string
  }).isRequired,
  favoritesEnabled: PropTypes.bool,
  userContent: PropTypes.shape({
    favorites: PropTypes.arrayOf(PropTypes.shape({})),
    savedViews: PropTypes.shape({})
  }),
  userActions: PropTypes.shape({
    change: PropTypes.func,
    serverSave: PropTypes.func,
    serverShow: PropTypes.func
  })
};
export { Header as TestHeader, useHeaderInitialization };
export default entityComposition(Header, ['settings', 'user'], {
  user: state => state.login.user,
  cabinets: state => state.settings.settings.cabinetDefinitions,
  tabGroups: state => state.settings.settings.tabGroupDefinitions,
  dashboards: state => {
    var _state$login;
    return (state === null || state === void 0 || (_state$login = state.login) === null || _state$login === void 0 || (_state$login = _state$login.user) === null || _state$login === void 0 || (_state$login = _state$login.superset) === null || _state$login === void 0 ? void 0 : _state$login.dashboards) || [];
  },
  settingsLoaded: state => state.settings.settings && Object.keys(state.settings.settings).length > 0,
  favoritesEnabled: state => {
    var _state$settings;
    return ((_state$settings = state.settings) === null || _state$settings === void 0 || (_state$settings = _state$settings.settings) === null || _state$settings === void 0 ? void 0 : _state$settings.favoritesEnabled) || false;
  },
  userContent: state => {
    var _state$user;
    return (_state$user = state.user) === null || _state$user === void 0 || (_state$user = _state$user.active) === null || _state$user === void 0 ? void 0 : _state$user.content;
  }
}, ['configBehavior']);